/**
 * Single source of truth for "this page is reachable without a CRM login".
 *
 * These are customer/partner-facing or token-gated pages: a proposal link, a
 * feedback form, a payslip. Nobody landing on one has (or should need) an admin
 * session, so nothing in the app may bounce them to the staff login screen.
 *
 * This list used to live inline in App.tsx's AuthGuard only. The 401 handler in
 * queryClient.ts had no equivalent check, so one admin-only query firing on a
 * public page (SetupHelperProvider's /api/saas/onboarding-state, which wraps the
 * whole app) hard-redirected paying customers off their own quote to
 * "Sign in to access your CRM dashboard". Both now read this one list.
 */
const PUBLIC_PREFIXES = [
  "/auth",
  "/portal",
  "/partner-portal",
  // The reseller (agency) console. Bearer-token auth of its own, never a CRM
  // cookie. NB the owner's admin page is "/resellers", which does NOT start with
  // this prefix — keep it that way or the admin page falls out of the login wall.
  "/reseller-portal",
  "/employee-portal", // NB: some employee routes are protected in their own right
  "/client-portal",
  "/reports/weekly",
  "/mobile",
  "/tradevault",
  "/review/", // Token-gated customer review page (no account)
  "/feedback/", // Token-gated customer feedback page (no account)
  "/outreach-report/", // Token-gated discovery report (no account)
  "/public/kitchen-studio", // Public kitchen studio (iframe)
  "/videos/", // Public video assets
  "/video/share/", // Public branded video share page
  "/proposal/", // Token-gated customer quote/proposal page (no account)
  "/build/", // Token-gated client view of a saved build (no prices)
  // Token-gated monthly report a client opens from their link. The owner's own
  // page is "/client-reports", which does NOT start with this — keep it that way.
  "/monthly-report/",
  "/partner-statement/", // Token-gated partner statement for one job (no account)
  "/job-hub/", // Token-gated public job hub page
  "/payslip/", // Signed-token public payslip view (no account)
  "/doc/", // Token-gated public document viewer (no account)
  // The owner's notes round-up, opened from the link in his own WhatsApp thread.
  // Prefix only: the /notes BOARD is an ordinary admin page and must stay behind
  // the login. Only /notes/<token> is public.
  "/notes/",
  // Home-screen quick-action tiles. Not public data — the page resolves the
  // session itself and sends you to the right login when there is none. It is
  // listed here so the global guard cannot bounce an employee-portal user (who
  // holds a Bearer token, not an admin cookie) to the staff login screen.
  "/go/",
];

const PUBLIC_EXACT = [
  "/mobile-auth",
  "/landing",
  "/sales-deck",
  "/sales-discovery",
  "/enquiry",
  "/enquiry.html",
  "/enquiery",
  "/enquiery.html",
  "/plumbing-leads",
  // Sister-brand versions of the same public lead form. Omitting these sent
  // every "Book Consultation Online" click on the landscaping site (74 links
  // across 20 pages) to the staff login screen, so the brand's only web
  // enquiry route was dead. Whenever a *-leads page is added to App.tsx it
  // must be added here too.
  "/landscaping-leads",
  "/electrician-leads",
  // The electricians site links this exact capitalised path (81 links, 19
  // pages). Registered as an alias route in App.tsx rather than rewriting the
  // links, so the fix does not need that site redeployed.
  "/Electrical-leads",
  "/partner-onboarding",
];

/** True when `pathname` is a public / token-gated page that needs no CRM session. */
export function isPublicPath(pathname: string): boolean {
  return (
    PUBLIC_EXACT.includes(pathname) ||
    PUBLIC_PREFIXES.some((prefix) => pathname.startsWith(prefix))
  );
}
